import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { getUsersList } from '../../services/user';
import type { IAdminUser, IPagination } from '../../interfaces';

interface UsersState {
  users: IAdminUser[];
  loading: boolean;
  error: string | null;
  pagination: IPagination;
  searchQuery: string;
}

const initialState: UsersState = {
  users: [],
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  },
  searchQuery: '',
};


export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (params?: { page?: number; limit?: number; search?: string; roles?: string[]; statuses?: string[]; fromDate?: string; toDate?: string; sortBy?: string; sortOrder?: 'ASC' | 'DESC' }) => {
    const response = await getUsersList(params);

    if (response.success && response.data) {
      return {
        users: response.data.users,
        pagination: response.data.pagination,
      };
    }
    throw new Error(response.message || 'Failed to fetch users');
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users;
        state.pagination = {
          page: Number(action.payload.pagination.page),
          limit: Number(action.payload.pagination.limit),
          total: Number(action.payload.pagination.total),
          totalPages: Number(action.payload.pagination.totalPages),
          hasNextPage: action.payload.pagination.hasNextPage,
          hasPrevPage: action.payload.pagination.hasPrevPage,
        };
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch users';
      });
  },
});

export const { setSearchQuery } = usersSlice.actions;
export default usersSlice.reducer;
